import { useEffect, useRef } from "react"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../constants/routes.ts"
import { type AuthState } from "../containers/LoginPage/redux/auth.type.ts"
import {
  type HandledErrorAction,
  createHandledError
} from "../errors/handled-error"
import {
  reportHandledError,
  reportUnexpectedFailure
} from "../observability/handled-errors"
import { errorSave } from "../redux/errors.slice"
import API, { request } from "../services/api"
import { type AppDispatch } from "../store.ts"
import utils from "../utils/utils"
import { useAppDispatch } from "./use-app-dispatch.ts"

const registrationPages = [
  "sign-up",
  "add-email",
  "restore-password",
  "check-mailbox",
  "verify-code",
  "success",
  "auth",
  "confirm-email-change",
  "whatsapp_login"
]

const innerPages = [
  "home",
  "dashboard",
  "reports",
  "invoices",
  "leads",
  "calendar",
  "projects",
  "customers",
  "subcontractors",
  "suppliers",
  "team_management",
  "settings",
  "confirm-email-change",
  "whatsapp_login",
  "available-time",
  "templates",
  "proposal",
  "proposals",
  "contract",
  "contracts",
  "document_templates",
  "proposal_templates",
  "contract_templates"
]

interface StartupAuthState {
  accessToken: AuthState["accessToken"]
  refreshToken: AuthState["refreshToken"]
  userInfo: AuthState["userInfo"] | null
}

interface RunInitialRouteGuardParams {
  auth: StartupAuthState
  dispatch: AppDispatch
  navigate: NavigateFunction
  pathname?: string
  search?: string
}

interface OAuthRedirectResponse {
  redirect_uri?: unknown
  error?: unknown
}

interface ContinueOAuthParams {
  nextUrl: string
  dispatch: AppDispatch
  navigate: NavigateFunction
  retryHref: string
}

export const OAUTH_CONTINUATION_FLOW = "startup.oauth-continuation"
export const STARTUP_GUARD_FLOW = "startup.route-guard"

const OAUTH_USER_CANCELLED = "access_denied"

const includesCurrentPathSegment = (pathname: string, segment: string) =>
  utils.includeCurrentLocation(pathname, segment)

const getNextUrl = (search: string) =>
  search?.includes("?next=") ? search?.split("?next=")?.[1] : null

const decodeNextUrl = (nextUrl: string) => {
  try {
    return decodeURIComponent(nextUrl)
  } catch (e) {
    console.error("Error decoding nextUrl:", e)
    return nextUrl
  }
}

const dispatchGoLink = (dispatch: AppDispatch, link: string) => {
  (dispatch as (action: unknown) => unknown)(utils.goLink(link))
}

const isUserCancellation = (nextUrl: string, data: unknown): boolean =>
  nextUrl.includes(`error=${OAUTH_USER_CANCELLED}`) ||
  (typeof data === "object" &&
    data !== null &&
    (data as OAuthRedirectResponse).error === OAUTH_USER_CANCELLED)

const isRedirectTarget = (value: unknown): value is string =>
  typeof value === "string" && /^https?:\/\//i.test(value)

const oauthAction = (
  status: number | null,
  retryHref: string
): HandledErrorAction =>
  status === 401 || status === 403
    ? { kind: "restart-login", href: RoutesPaths.auth.login }
    : { kind: "retry", href: retryHref }

const continueOAuth = async ({
  nextUrl,
  dispatch,
  navigate,
  retryHref
}: ContinueOAuthParams): Promise<void> => {
  const response = await request.api.post<OAuthRedirectResponse>(nextUrl, {
    maxRedirects: 0,
    validateStatus: (status: number) => status >= 200 && status < 400
  })

  if (
    response?.problem === "CANCEL_ERROR" ||
    isUserCancellation(nextUrl, response?.data)
  ) {
    navigate(RoutesPaths.home, { replace: true })
    return
  }

  const redirectUri = response?.data?.redirect_uri
  if (response?.ok && isRedirectTarget(redirectUri)) {
    dispatchGoLink(dispatch, redirectUri)
    return
  }

  const status = typeof response?.status === "number" ? response.status : null
  const error = createHandledError(response ?? {}, {
    flow: OAUTH_CONTINUATION_FLOW,
    endpoint: nextUrl,
    method: "post",
    code: response?.ok ? "malformed" : "oauth_continuation",
    action: oauthAction(status, retryHref)
  })
  reportHandledError(error)
  dispatch(errorSave(error))
  navigate(RoutesPaths.home, { replace: true })
}

const runRouteGuard = async ({
  auth,
  dispatch,
  navigate,
  pathname,
  search
}: Required<RunInitialRouteGuardParams>): Promise<boolean> => {
  const { accessToken, refreshToken, userInfo } = auth

  if (accessToken) API.updateAccessToken(accessToken, refreshToken)

  if (accessToken && userInfo) {
    if (
      includesCurrentPathSegment(pathname, "sign-up") &&
      pathname !== "/sign-up"
    ) {
      return true
    }

    if (
      pathname !== "/" &&
      innerPages.some((page) => includesCurrentPathSegment(pathname, page))
    ) {
      const nextUrl = getNextUrl(search)

      if (nextUrl) {
        await continueOAuth({
          nextUrl: decodeNextUrl(nextUrl),
          dispatch,
          navigate,
          retryHref: `${pathname}${search || ""}`
        })
      }

      return true
    }

    if (userInfo["email_added"]) {
      navigate(RoutesPaths.home + (search || ""))
    } else {
      navigate("/add-email")
    }

    return true
  }

  if (
    registrationPages.some((page) => includesCurrentPathSegment(pathname, page))
  ) {
    if (pathname === "/sign-up") {
      navigate(`/sign-up${search || ""}`)
    }

    return true
  }

  if (
    !["/", "/login"]?.includes(pathname) &&
    innerPages.some((page) => includesCurrentPathSegment(pathname, page))
  ) {
    navigate("/login", { replace: true })
    return true
  }

  const url =
    pathname === "/" ? "/login" + (search || "") : pathname + (search || "")
  navigate(url, { replace: true })

  return true
}

export const runInitialRouteGuard = async ({
  auth,
  dispatch,
  navigate,
  pathname = document.location.pathname,
  search = document.location.search
}: RunInitialRouteGuardParams): Promise<boolean> => {
  try {
    return await runRouteGuard({ auth, dispatch, navigate, pathname, search })
  } catch (failure) {
    const nextUrl = getNextUrl(search)
    reportUnexpectedFailure(failure, {
      flow: STARTUP_GUARD_FLOW,
      endpoint: nextUrl ? decodeNextUrl(nextUrl) : pathname
    })
    dispatch(
      errorSave(
        createHandledError(
          {},
          {
            flow: STARTUP_GUARD_FLOW,
            endpoint: pathname,
            code: "unknown",
            action: { kind: "retry", href: `${pathname}${search || ""}` }
          }
        )
      )
    )
    navigate(RoutesPaths.home, { replace: true })
    return false
  }
}

export const useStartup = (auth: StartupAuthState) => {
  const hasRunRef = useRef(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    if (hasRunRef.current) return
    hasRunRef.current = true

    void runInitialRouteGuard({ auth, dispatch, navigate })
  }, [auth, dispatch, navigate])
}
